<script setup lang="ts">
import type { JournalListItemDTO } from '~/shared/api/client/journals/journals.dto';
import { getJournalIssueUrl } from '~/shared/lib/journal-routes.const';
import JournalCover from './JournalCover.vue';
import JournalIssueButtons from './JournalIssueButtons.vue';

import { getIssueMeta } from '../lib/getIssueMeta';

const props = defineProps<{
  issue: JournalListItemDTO;
}>();

const meta = computed(() => getIssueMeta(props.issue));

// Filled in from the cover when it is readable, otherwise the CSS default stays
const accent = ref<string>();
</script>

<template>
  <section :class="$style.hero">
    <div
      :class="$style.coverArea"
      :style="accent ? { '--cover-accent': accent } : undefined"
    >
      <div :class="$style.rays" />
      <!-- The hero issue is not repeated in the grid, so this is its only link -->
      <NuxtLink :to="getJournalIssueUrl(issue)" :class="$style.coverLink">
        <JournalCover
          :title="issue.title"
          :src="issue.cover?.file_url"
          :class="$style.cover"
          @accent="accent = $event"
        />
      </NuxtLink>
    </div>
    <NuxtLink :to="getJournalIssueUrl(issue)" :class="$style.titleLink">
      <h2 :class="$style.title">{{ issue.title }}</h2>
    </NuxtLink>
    <p v-if="meta" :class="$style.meta">{{ meta }}</p>
    <p v-if="issue.short_description" :class="$style.description">
      {{ issue.short_description }}
    </p>
    <JournalIssueButtons
      :issue="issue"
      size="standard"
      equal-width
      :class="$style.buttons"
    />
  </section>
</template>

<style module lang="scss">
.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  // No padding of its own above: the chips already leave their gap, and the
  // cover area alone sets the distance from them
  padding: 0 var(--space-2-5x) var(--space-6x);
}

.coverArea {
  // Backdrop tone; meant to be fed from the cover, falls back to CRE cream
  --cover-accent: #f6ecd7;

  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: var(--space-1x) 0 var(--space-4x);
}

// Two wedges opening left and right from behind the cover, as in the layout
.rays {
  position: absolute;
  inset: 0;
  pointer-events: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 46%;
  }

  &::before {
    left: 0;
    clip-path: polygon(100% 30%, 100% 56%, 0 100%, 0 0);
    background: linear-gradient(
      to left,
      var(--cover-accent) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  &::after {
    right: 0;
    clip-path: polygon(0 30%, 0 56%, 100% 100%, 100% 0);
    background: linear-gradient(
      to right,
      var(--cover-accent) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }
}

.coverLink {
  display: flex;
  justify-content: center;
  width: 100%;
}

.cover {
  --thickness: 24px;

  max-width: 340px;
}

.titleLink {
  color: inherit;
  text-decoration: none;
}

.title {
  @include text_h3;
  color: var(--color-black-1);
  margin: var(--space-4x) 0 0;
  text-align: center;
  transition: color 0.2s;

  .titleLink:hover & {
    color: var(--color-red-1);
  }
}

.meta {
  @include text_body1_medium;
  color: var(--color-black-2);
  margin: var(--space-2x) 0 0;
  text-align: center;
}

.description {
  @include text_body1_large_medium;
  color: var(--color-black-2);
  max-width: 591px;
  margin: var(--space-2x) 0 0;
  text-align: center;
}

// The gap before the buttons belongs to the buttons, not to the description:
// a description is optional, and while it carried the gap an issue without one
// put its buttons flush against the title
.buttons {
  margin-top: var(--space-3x);
}

@media (max-width: 599px) {
  .hero {
    padding: 0 var(--space-2x) var(--space-4x);
  }

  .cover {
    max-width: 220px;
  }

  .title {
    @include text_h5;
  }

  .meta {
    @include text_caption1_medium;
    margin-top: var(--space-1-5x);
  }

  .description {
    @include text_body2_medium;
  }
}
</style>
